import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../common/hooks/useAuth';
import { useProducer } from '../../common/hooks/useProducer';
import { farmImagesAPI, websitesAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Input from '../../common/components/Input';
import Card from '../../common/components/Card';
import MapPicker from '../../common/components/MapPicker';
import ImageUpload from '../../common/components/ImageUpload';
import ukData from '../../data/uk_counties_towns.json';
import './FarmDetailsScreen.css';

const COUNTRIES = ['England', 'Wales', 'Scotland', 'Northern Ireland'];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SECTIONS = [
  { id: 'details', name: 'Details' },
  { id: 'images', name: 'Images' },
  { id: 'access', name: 'Access' },
  { id: 'address', name: 'Address' },
  { id: 'location', name: 'Location' },
  { id: 'contact', name: 'Contact' },
  { id: 'websites', name: 'Websites' },
];

const FarmDetailsScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, update, isUpdating } = useProducer();

  const [currentSection, setCurrentSection] = useState('details');
  const [formData, setFormData] = useState({
    farmName: '',
    farmDescription: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    town: '',
    county: '',
    postcode: '',
    country: 'England',
    contactPhone: '',
    latitude: null,
    longitude: null,
    products: '',
    delivery: false,
    collection: false,
    shopOpen: false,
    specialties: '',
    farmVisits: false,
    openingTimes: '',
    instagram: '',
    facebook: '',
    twitter: '',
  });

  // Store initial form data to detect changes
  const [initialFormData, setInitialFormData] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Combo box state for county and town
  const [availableCounties, setAvailableCounties] = useState([]);
  const [availableTowns, setAvailableTowns] = useState([]);
  const [countyInputValue, setCountyInputValue] = useState('');
  const [townInputValue, setTownInputValue] = useState('');
  const [showCountySuggestions, setShowCountySuggestions] = useState(false);
  const [showTownSuggestions, setShowTownSuggestions] = useState(false);
  const countyDropdownRef = useRef(null);
  const townDropdownRef = useRef(null);

  // Opening times as structured data
  const [openingTimesData, setOpeningTimesData] = useState({
    Monday: '',
    Tuesday: '',
    Wednesday: '',
    Thursday: '',
    Friday: '',
    Saturday: '',
    Sunday: '',
  });

  // Farm images state
  const [uploadingImage, setUploadingImage] = useState({
    logo: false,
    favicon: false,
    banner: false,
  });

  // Fetch farm images
  const { data: farmImages, refetch: refetchImages } = useQuery(
    ['farmImages', profile?.id],
    async () => {
      const response = await farmImagesAPI.list();
      return response.data.data;
    },
    {
      enabled: !!profile?.id,
      onError: (error) => {
        console.error('Error fetching farm images:', error);
      },
    }
  );

  // Image upload mutation
  const uploadImageMutation = useMutation(
    ({ imageType, file }) => farmImagesAPI.upload(imageType, file),
    {
      onMutate: ({ imageType }) => {
        setUploadingImage(prev => ({ ...prev, [imageType]: true }));
      },
      onSuccess: (response, { imageType }) => {
        toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} uploaded successfully!`);
        refetchImages();
      },
      onError: (error, { imageType }) => {
        console.error('Upload error:', error);
        toast.error(error.data?.error || error.message || `Failed to upload ${imageType}`);
      },
      onSettled: (data, error, variables) => {
        setUploadingImage(prev => ({ ...prev, [variables.imageType]: false }));
      },
    }
  );

  // Image delete mutation
  const deleteImageMutation = useMutation(
    (imageType) => farmImagesAPI.delete(imageType),
    {
      onSuccess: (response, imageType) => {
        toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} deleted successfully!`);
        refetchImages();
      },
      onError: (error, imageType) => {
        console.error('Delete error:', error);
        toast.error(error.data?.error || `Failed to delete ${imageType}`);
      },
    }
  );

  // Get image URL by type
  const getImageUrl = (imageType) => {
    const image = farmImages?.find(img => img.image_type === imageType);
    return image?.url || null;
  };

  // Handle image upload
  const handleImageUpload = (imageType, file) => {
    uploadImageMutation.mutate({ imageType, file });
  };

  // Handle image delete
  const handleImageDelete = (imageType) => {
    if (window.confirm(`Are you sure you want to delete your ${imageType}?`)) {
      deleteImageMutation.mutate(imageType);
    }
  };

  // Websites state and management
  const [websites, setWebsites] = useState([]);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [newWebsite, setNewWebsite] = useState({ url: '', websiteType: '', isPrimary: false });
  const [websiteUrlError, setWebsiteUrlError] = useState('');
  const [editingUrlError, setEditingUrlError] = useState('');

  // URL validation and normalization helpers
  const normalizeUrl = (url) => {
    const trimmed = url.trim();
    if (!trimmed) return '';

    // If no protocol, add https://
    if (!trimmed.includes('://')) {
      return `https://${trimmed}`;
    }

    return trimmed;
  };

  const validateUrl = (url) => {
    if (!url.trim()) return '';

    // Check if it has a protocol
    if (!url.includes('://')) {
      return ''; // Will be auto-fixed with https://
    }

    // Basic URL validation
    try {
      const urlObj = new URL(url);

      // Check for valid protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return 'URL must use http:// or https://';
      }

      // Check for valid domain
      if (!urlObj.hostname || urlObj.hostname.length < 3) {
        return 'Invalid domain name';
      }

      // Check for valid domain format (at least one dot)
      if (!urlObj.hostname.includes('.') && urlObj.hostname !== 'localhost') {
        return 'Domain must contain at least one dot (e.g., example.com)';
      }

      return ''; // Valid
    } catch (e) {
      return 'Invalid URL format';
    }
  };

  // Fetch websites
  const { refetch: refetchWebsites } = useQuery(
    ['websites', profile?.id],
    async () => {
      const response = await websitesAPI.list();
      return response.data.data;
    },
    {
      enabled: !!profile?.id,
      onSuccess: (data) => {
        setWebsites(data || []);
      },
      onError: (error) => {
        console.error('Error fetching websites:', error);
      },
    }
  );

  // Create website mutation
  const createWebsiteMutation = useMutation(
    (data) => websitesAPI.create(data),
    {
      onSuccess: () => {
        toast.success('Website added successfully!');
        setNewWebsite({ url: '', websiteType: '', isPrimary: false });
        setWebsiteUrlError('');
        refetchWebsites();
      },
      onError: (error) => {
        console.error('Create website error:', error);
        toast.error(error.data?.message || 'Failed to add website');
      },
    }
  );

  // Update website mutation
  const updateWebsiteMutation = useMutation(
    ({ id, data }) => websitesAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('Website updated successfully!');
        setEditingWebsite(null);
        setEditingUrlError('');
        refetchWebsites();
      },
      onError: (error) => {
        console.error('Update website error:', error);
        toast.error(error.data?.message || 'Failed to update website');
      },
    }
  );

  // Delete website mutation
  const deleteWebsiteMutation = useMutation(
    (id) => websitesAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('Website deleted successfully!');
        refetchWebsites();
      },
      onError: (error) => {
        console.error('Delete website error:', error);
        toast.error(error.data?.message || 'Failed to delete website');
      },
    }
  );

  // Handle new website URL change
  const handleNewWebsiteUrlChange = (value) => {
    setNewWebsite({ ...newWebsite, url: value });
    const error = validateUrl(value);
    setWebsiteUrlError(error);
  };

  // Handle editing website URL change
  const handleEditingWebsiteUrlChange = (value) => {
    setEditingWebsite({ ...editingWebsite, url: value });
    const error = validateUrl(value);
    setEditingUrlError(error);
  };

  // Handle add website
  const handleAddWebsite = () => {
    if (!newWebsite.url.trim()) {
      toast.error('Please enter a website URL');
      return;
    }

    // Normalize URL before submitting
    const normalizedUrl = normalizeUrl(newWebsite.url);
    const error = validateUrl(normalizedUrl);

    if (error) {
      setWebsiteUrlError(error);
      toast.error('Please fix the URL errors before submitting');
      return;
    }

    createWebsiteMutation.mutate({
      ...newWebsite,
      url: normalizedUrl,
    });
  };

  // Handle update website
  const handleUpdateWebsite = (id, updates) => {
    // Normalize URL before submitting if URL is being updated
    if (updates.url) {
      const normalizedUrl = normalizeUrl(updates.url);
      const error = validateUrl(normalizedUrl);

      if (error) {
        setEditingUrlError(error);
        toast.error('Please fix the URL errors before submitting');
        return;
      }

      updates = { ...updates, url: normalizedUrl };
    }

    updateWebsiteMutation.mutate({ id, data: updates });
  };

  // Handle delete website
  const handleDeleteWebsite = (id) => {
    if (window.confirm('Are you sure you want to delete this website?')) {
      deleteWebsiteMutation.mutate(id);
    }
  };

  // Handle set primary website
  const handleSetPrimaryWebsite = (id) => {
    updateWebsiteMutation.mutate({
      id,
      data: { isPrimary: true }
    });
  };

  // Load profile data when it's available
  useEffect(() => {
    if (profile) {
      const profileData = {
        farmName: profile.farm_name || '',
        farmDescription: profile.farm_description || '',
        addressLine1: profile.address_line1 || '',
        addressLine2: profile.address_line2 || '',
        addressLine3: profile.address_line3 || '',
        town: profile.town || '',
        county: profile.county || '',
        postcode: profile.postcode || '',
        country: profile.country || '',
        contactPhone: profile.contact_phone || '',
        latitude: profile.latitude ? parseFloat(profile.latitude) : null,
        longitude: profile.longitude ? parseFloat(profile.longitude) : null,
        products: profile.products || '',
        delivery: profile.delivery === 1,
        collection: profile.collection === 1,
        shopOpen: profile.shop_open === 1,
        specialties: profile.specialties || '',
        farmVisits: profile.farm_visits === 1,
        openingTimes: profile.opening_times || '',
        instagram: profile.instagram || '',
        facebook: profile.facebook || '',
        twitter: profile.twitter || '',
      };
      setFormData(profileData);
      setInitialFormData(profileData);

      // Set combo box values
      setCountyInputValue(profile.county || '');
      setTownInputValue(profile.town || '');

      // Parse opening times JSON
      if (profile.opening_times) {
        try {
          const parsed = JSON.parse(profile.opening_times);
          setOpeningTimesData(parsed);
        } catch (e) {
          // If it's not JSON, it's the old format - ignore it
          console.log('Opening times not in JSON format');
        }
      }
    }
  }, [profile]);

  // Update available counties when country changes
  useEffect(() => {
    if (formData.country && ukData[formData.country]) {
      const counties = Object.keys(ukData[formData.country]);
      setAvailableCounties(counties);
    } else {
      setAvailableCounties([]);
    }
  }, [formData.country]);

  // Update available towns when county changes
  useEffect(() => {
    if (formData.country && formData.county && ukData[formData.country] && ukData[formData.country][formData.county]) {
      setAvailableTowns(ukData[formData.country][formData.county]);
    } else {
      setAvailableTowns([]);
    }
  }, [formData.country, formData.county]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (townDropdownRef.current && !townDropdownRef.current.contains(event.target)) {
        setShowTownSuggestions(false);
      }
      if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target)) {
        setShowCountySuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle county combo box
  const handleCountyInputChange = (value) => {
    setCountyInputValue(value);
    setFormData(prev => ({ ...prev, county: value }));
    setShowCountySuggestions(true);
  };

  const handleCountySelect = (selectedCounty) => {
    setFormData(prev => ({ ...prev, county: selectedCounty }));
    setCountyInputValue(selectedCounty);
    setShowCountySuggestions(false);
    // Reset town when county changes
    setFormData(prev => ({ ...prev, town: '' }));
    setTownInputValue('');
  };

  const getFilteredCounties = () => {
    if (!countyInputValue) return availableCounties;
    return availableCounties.filter(c =>
      c.toLowerCase().includes(countyInputValue.toLowerCase())
    );
  };

  // Handle town combo box
  const handleTownInputChange = (value) => {
    setTownInputValue(value);
    setFormData(prev => ({ ...prev, town: value }));
    setShowTownSuggestions(true);
  };

  const handleTownSelect = (selectedTown) => {
    setFormData(prev => ({ ...prev, town: selectedTown }));
    setTownInputValue(selectedTown);
    setShowTownSuggestions(false);
  };

  const getFilteredTowns = () => {
    if (!townInputValue) return availableTowns;
    return availableTowns.filter(t =>
      t.toLowerCase().includes(townInputValue.toLowerCase())
    );
  };

  // Handle opening times for each day
  const handleOpeningTimeChange = (day, value) => {
    setOpeningTimesData(prev => ({
      ...prev,
      [day]: value,
    }));
  };

  // Handle map location change
  const handleLocationChange = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updateData = {
      farmName: formData.farmName,
      farmDescription: formData.farmDescription || '',
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2 || '',
      addressLine3: formData.addressLine3 || '',
      town: formData.town,
      county: formData.county,
      postcode: formData.postcode,
      country: formData.country,
      contactPhone: formData.contactPhone || '',
      latitude: formData.latitude,
      longitude: formData.longitude,
      products: formData.products || '',
      delivery: formData.delivery ? 1 : 0,
      collection: formData.collection ? 1 : 0,
      shopOpen: formData.shopOpen ? 1 : 0,
      specialties: formData.specialties || '',
      farmVisits: formData.farmVisits ? 1 : 0,
      openingTimes: JSON.stringify(openingTimesData),
      instagram: formData.instagram || '',
      facebook: formData.facebook || '',
      twitter: formData.twitter || '',
    };

    console.log('📤 Updating farm details:', updateData);

    update(updateData, {
      onSuccess: () => {
        console.log('✅ Farm details updated successfully');
        // Update initial form data to reflect saved state
        setInitialFormData({ ...formData });
        setShowSuccessDialog(true);
      },
      onError: (error) => {
        console.error('❌ Update error:', error);
      },
    });
  };

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    // No need to reload - React Query automatically refetches profile data
  };

  const handleCancel = () => {
    navigate(`/${profile?.url_friendly_name}`);
  };

  // Check if form data has changed
  const hasChanges = () => {
    if (!initialFormData) return false;

    return Object.keys(formData).some(key => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      // Handle null/undefined/empty string comparisons
      if (currentValue === initialValue) return false;
      if ((currentValue === '' || currentValue === null) && (initialValue === '' || initialValue === null)) return false;

      return true;
    });
  };

  // Section navigation
  const getCurrentSectionIndex = () => SECTIONS.findIndex(s => s.id === currentSection);

  const goToPreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      setCurrentSection(SECTIONS[currentIndex - 1].id);
    }
  };

  const goToNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentIndex + 1].id);
    }
  };

  const getPreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    return currentIndex > 0 ? SECTIONS[currentIndex - 1] : null;
  };

  const getNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    return currentIndex < SECTIONS.length - 1 ? SECTIONS[currentIndex + 1] : null;
  };

  // Render different sections
  const renderDetailsSection = () => (
    <>
      <Input
        label="Farm Name"
        name="farmName"
        value={formData.farmName}
        onChange={handleChange}
        placeholder="e.g., Richies Farm"
        required
      />

      <div className="form-group">
        <label htmlFor="farmDescription">Farm Description (Optional)</label>
        <textarea
          id="farmDescription"
          name="farmDescription"
          value={formData.farmDescription}
          onChange={handleChange}
          placeholder="Tell us about your farm..."
          rows="4"
        />
      </div>

      <div className="form-group">
        <label htmlFor="products">Products (Optional)</label>
        <textarea
          id="products"
          name="products"
          value={formData.products}
          onChange={handleChange}
          placeholder="List your products (comma-separated or one per line)"
          rows="3"
        />
        <small className="help-text">
          e.g., Organic vegetables, Free-range eggs, Raw milk
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="specialties">Farm Specialties (Optional)</label>
        <textarea
          id="specialties"
          name="specialties"
          value={formData.specialties}
          onChange={handleChange}
          placeholder="List your specialties (comma-separated or one per line)"
          rows="3"
        />
        <small className="help-text">
          e.g., Organic farming, Heritage breeds, Permaculture
        </small>
      </div>
    </>
  );

  const renderImagesSection = () => (
    <>
      <p className="section-description">
        Upload images to personalize your farm profile
      </p>

      <div className="images-grid">
        <ImageUpload
          label="Farm Logo"
          currentImageUrl={getImageUrl('logo')}
          onUpload={(file) => handleImageUpload('logo', file)}
          onDelete={() => handleImageDelete('logo')}
          isUploading={uploadingImage.logo}
          maxSize="100KB"
          dimensions="Recommended: 500x500px"
          acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
          maxSizeBytes={102400}
        />

        <ImageUpload
          label="Favicon"
          currentImageUrl={getImageUrl('favicon')}
          onUpload={(file) => handleImageUpload('favicon', file)}
          onDelete={() => handleImageDelete('favicon')}
          isUploading={uploadingImage.favicon}
          maxSize="100KB"
          dimensions="Recommended: 256x256px"
          acceptedFormats={['image/png', 'image/x-icon', 'image/vnd.microsoft.icon']}
          maxSizeBytes={102400}
        />

        <ImageUpload
          label="Banner Image"
          currentImageUrl={getImageUrl('banner')}
          onUpload={(file) => handleImageUpload('banner', file)}
          onDelete={() => handleImageDelete('banner')}
          isUploading={uploadingImage.banner}
          maxSize="1MB"
          dimensions="Recommended: 2000x1000px"
          acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
          maxSizeBytes={1048576}
        />
      </div>
    </>
  );

  const renderAccessSection = () => (
    <>
      <div className="form-group toggle-switches-group">
        <label className="checkbox-label">
          <span>Delivery Available</span>
          <input
            type="checkbox"
            name="delivery"
            checked={formData.delivery}
            onChange={handleChange}
          />
          <div className="toggle-switch"></div>
        </label>

        <label className="checkbox-label">
          <span>Collection Available</span>
          <input
            type="checkbox"
            name="collection"
            checked={formData.collection}
            onChange={handleChange}
          />
          <div className="toggle-switch"></div>
        </label>

        <label className="checkbox-label">
          <span>Shop Open to Public</span>
          <input
            type="checkbox"
            name="shopOpen"
            checked={formData.shopOpen}
            onChange={handleChange}
          />
          <div className="toggle-switch"></div>
        </label>

        <label className="checkbox-label">
          <span>Farm Visits Allowed</span>
          <input
            type="checkbox"
            name="farmVisits"
            checked={formData.farmVisits}
            onChange={handleChange}
          />
          <div className="toggle-switch"></div>
        </label>
      </div>

      <div className="form-group">
        <label>Opening Times (Optional)</label>
        <small className="help-text" style={{ marginBottom: '12px', display: 'block' }}>
          Enter opening hours for each day. Leave blank for days you're closed.
        </small>
        <div className="opening-times-list">
          {DAYS_OF_WEEK.map(day => (
            <div key={day} className="opening-time-row">
              <label className="day-label">{day}</label>
              <input
                type="text"
                value={openingTimesData[day]}
                onChange={(e) => handleOpeningTimeChange(day, e.target.value)}
                placeholder="e.g., 9:00 AM - 5:00 PM or Closed"
                className="opening-time-input"
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const renderAddressSection = () => (
    <>
      <div className="form-group">
        <label htmlFor="country">Country</label>
        <select
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          required
        >
          <option value="">Select a country</option>
          {COUNTRIES.map(country => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group combo-container" ref={countyDropdownRef}>
        <label htmlFor="county">County</label>
        <input
          id="county"
          type="text"
          value={countyInputValue}
          onChange={(e) => handleCountyInputChange(e.target.value)}
          onFocus={() => setShowCountySuggestions(true)}
          placeholder="Select or type county"
          required
        />
        {showCountySuggestions && getFilteredCounties().length > 0 && (
          <div className="combo-suggestions">
            {getFilteredCounties().map((c, index) => (
              <div
                key={index}
                className="combo-suggestion-item"
                onClick={() => handleCountySelect(c)}
              >
                {c}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-group combo-container" ref={townDropdownRef}>
        <label htmlFor="town">Town</label>
        <input
          id="town"
          type="text"
          value={townInputValue}
          onChange={(e) => handleTownInputChange(e.target.value)}
          onFocus={() => setShowTownSuggestions(true)}
          placeholder="Select or type town"
          required
        />
        {showTownSuggestions && getFilteredTowns().length > 0 && (
          <div className="combo-suggestions">
            {getFilteredTowns().slice(0, 50).map((t, index) => (
              <div
                key={index}
                className="combo-suggestion-item"
                onClick={() => handleTownSelect(t)}
              >
                {t}
              </div>
            ))}
          </div>
        )}
      </div>

      <Input
        label="Postcode"
        name="postcode"
        value={formData.postcode}
        onChange={handleChange}
        placeholder="e.g., SW1A 1AA"
        required
      />

      <Input
        label="Address Line 1"
        name="addressLine1"
        value={formData.addressLine1}
        onChange={handleChange}
        placeholder="Street address"
        required
      />

      <Input
        label="Address Line 2 (Optional)"
        name="addressLine2"
        value={formData.addressLine2}
        onChange={handleChange}
        placeholder="Building, unit, etc."
      />

      <Input
        label="Address Line 3 (Optional)"
        name="addressLine3"
        value={formData.addressLine3}
        onChange={handleChange}
        placeholder="Additional address info"
      />
    </>
  );

  const renderLocationSection = () => (
    <>
      <p className="section-description">
        Your farm's location on the map. You can drag the marker or click on the map to adjust the position.
      </p>

      <MapPicker
        latitude={formData.latitude}
        longitude={formData.longitude}
        onLocationChange={handleLocationChange}
        height="500px"
      />

      {formData.latitude && formData.longitude && (
        <div className="coordinates-display">
          <small>
            Coordinates: {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
          </small>
        </div>
      )}
    </>
  );

  const renderContactSection = () => (
    <>
      <div className="form-info">
        <p>Email: <strong>{user?.email}</strong></p>
      </div>

      <Input
        label="Contact Phone (Optional)"
        name="contactPhone"
        value={formData.contactPhone}
        onChange={handleChange}
        placeholder="Phone number"
        type="tel"
      />

      <h3 style={{ marginTop: '24px', marginBottom: '16px' }}>Social Media (Optional)</h3>

      <Input
        label="Instagram"
        name="instagram"
        value={formData.instagram}
        onChange={handleChange}
        placeholder="Instagram username or URL"
      />

      <Input
        label="Facebook"
        name="facebook"
        value={formData.facebook}
        onChange={handleChange}
        placeholder="Facebook page URL"
      />

      <Input
        label="Twitter"
        name="twitter"
        value={formData.twitter}
        onChange={handleChange}
        placeholder="Twitter username or URL"
      />
    </>
  );

  const renderWebsitesSection = () => (
    <>
      <p className="section-description">
        Add your farm's websites and mark one as primary.
      </p>

      {/* Existing websites list */}
      {websites.length > 0 && (
        <div className="websites-list" style={{ marginBottom: '24px' }}>
          {websites.map((website) => (
            <div key={website.id} className="website-item" style={{
              padding: '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              marginBottom: '12px',
              backgroundColor: website.is_primary ? '#f0f9ff' : '#fff'
            }}>
              {editingWebsite?.id === website.id ? (
                // Edit mode
                <div>
                  <div>
                    <Input
                      label="Website URL"
                      value={editingWebsite.url}
                      onChange={(e) => handleEditingWebsiteUrlChange(e.target.value)}
                      placeholder="example.com or https://example.com"
                      type="url"
                    />
                    {editingUrlError && (
                      <div style={{
                        color: '#dc2626',
                        fontSize: '14px',
                        marginTop: '4px',
                        marginBottom: '12px'
                      }}>
                        {editingUrlError}
                      </div>
                    )}
                    {editingWebsite.url && !editingWebsite.url.includes('://') && !editingUrlError && (
                      <div style={{
                        color: '#2563eb',
                        fontSize: '14px',
                        marginTop: '4px',
                        marginBottom: '12px'
                      }}>
                        Will be saved as: https://{editingWebsite.url}
                      </div>
                    )}
                  </div>
                  <Input
                    label="Website Type (Optional)"
                    value={editingWebsite.website_type || ''}
                    onChange={(e) => setEditingWebsite({ ...editingWebsite, website_type: e.target.value })}
                    placeholder="e.g., Shop, Blog, Portfolio"
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <Button
                      type="button"
                      onClick={() => handleUpdateWebsite(editingWebsite.id, {
                        url: editingWebsite.url,
                        websiteType: editingWebsite.website_type,
                      })}
                      size="small"
                      disabled={!!editingUrlError}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setEditingWebsite(null);
                        setEditingUrlError('');
                      }}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View mode
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div>
                      <a
                        href={website.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '16px', fontWeight: '500', color: '#2563eb', textDecoration: 'none' }}
                      >
                        {website.url}
                      </a>
                      {website.is_primary === 1 && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Primary
                        </span>
                      )}
                      {website.website_type && (
                        <span style={{
                          display: 'block',
                          marginTop: '4px',
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          Type: {website.website_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <Button
                      type="button"
                      onClick={() => setEditingWebsite(website)}
                      size="small"
                      variant="secondary"
                    >
                      Edit
                    </Button>
                    {website.is_primary !== 1 && (
                      <Button
                        type="button"
                        onClick={() => handleSetPrimaryWebsite(website.id)}
                        size="small"
                        variant="secondary"
                      >
                        Set as Primary
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={() => handleDeleteWebsite(website.id)}
                      size="small"
                      variant="secondary"
                      style={{ color: '#dc2626' }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new website */}
      <div style={{
        padding: '16px',
        border: '2px dashed #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fafafa'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>Add New Website</h3>
        <div>
          <Input
            label="Website URL"
            value={newWebsite.url}
            onChange={(e) => handleNewWebsiteUrlChange(e.target.value)}
            placeholder="example.com or https://example.com"
            type="url"
          />
          {websiteUrlError && (
            <div style={{
              color: '#dc2626',
              fontSize: '14px',
              marginTop: '4px',
              marginBottom: '12px'
            }}>
              {websiteUrlError}
            </div>
          )}
          {newWebsite.url && !newWebsite.url.includes('://') && !websiteUrlError && (
            <div style={{
              color: '#2563eb',
              fontSize: '14px',
              marginTop: '4px',
              marginBottom: '12px'
            }}>
              Will be saved as: https://{newWebsite.url}
            </div>
          )}
        </div>
        <Input
          label="Website Type (Optional)"
          value={newWebsite.websiteType}
          onChange={(e) => setNewWebsite({ ...newWebsite, websiteType: e.target.value })}
          placeholder="e.g., Shop, Blog, Portfolio"
        />
        <div className="form-group" style={{ marginTop: '12px' }}>
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={newWebsite.isPrimary}
              onChange={(e) => setNewWebsite({ ...newWebsite, isPrimary: e.target.checked })}
            />
            <span>Set as primary website</span>
          </label>
        </div>
        <Button
          type="button"
          onClick={handleAddWebsite}
          loading={createWebsiteMutation.isLoading}
          disabled={!newWebsite.url.trim() || !!websiteUrlError}
          style={{ marginTop: '12px' }}
        >
          Add Website
        </Button>
      </div>
    </>
  );

  if (!profile) {
    return (
      <div className="farm-details-screen">
        <div className="farm-details-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const currentSectionData = SECTIONS.find(s => s.id === currentSection);
  const previousSection = getPreviousSection();
  const nextSection = getNextSection();

  return (
    <div className="farm-details-screen">
      <div className="farm-details-container">
        <div className="farm-details-header">
          <h1>Farm Details</h1>
          <p>Update your farm information and location</p>
        </div>

        <Card>
          {/* Section Navigation */}
          <div className="section-navigation">
            <button
              type="button"
              className="nav-arrow"
              onClick={goToPreviousSection}
              disabled={!previousSection}
            >
              ←
            </button>

            <div className="section-info">
              {previousSection && (
                <span className="section-label prev">{previousSection.name}</span>
              )}
              <span className="section-label current">{currentSectionData.name}</span>
              {nextSection && (
                <span className="section-label next">{nextSection.name}</span>
              )}
            </div>

            <button
              type="button"
              className="nav-arrow"
              onClick={goToNextSection}
              disabled={!nextSection}
            >
              →
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Conditional Section Rendering */}
            <div className="form-content">
              {currentSection === 'details' && renderDetailsSection()}
              {currentSection === 'images' && renderImagesSection()}
              {currentSection === 'access' && renderAccessSection()}
              {currentSection === 'address' && renderAddressSection()}
              {currentSection === 'location' && renderLocationSection()}
              {currentSection === 'contact' && renderContactSection()}
              {currentSection === 'websites' && renderWebsitesSection()}
            </div>

            {/* Form Actions - Always at bottom */}
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isUpdating}
                disabled={!hasChanges() || isUpdating}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </Card>
      </div>

      {/* Success Dialog */}
      {showSuccessDialog && (
        <div className="modal-overlay">
          <div className="success-dialog">
            <div className="success-dialog-header">
              <div className="success-icon">✓</div>
              <h2>Farm Details Updated!</h2>
            </div>
            
            <div className="success-dialog-content">
              <p className="success-message">
                Your farm details have been successfully updated.
              </p>
            </div>
            
            <div className="success-dialog-actions">
              <Button onClick={handleDialogClose} size="large" style={{ width: '100%' }}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmDetailsScreen;
